import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Send, Loader2, Check, ChevronLeft, ChevronRight, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { fetchStateSettimana, replanTargetPeriodo, copyOpenActions } from '../lib/api';
import { useCoachChat } from '../lib/useCoachChat';
import SendDigestButton from './nav/SendDigestButton';
import CoachStatePanel from './nav/CoachStatePanel';
import CoachStatePanelSkeleton from './nav/CoachStatePanelSkeleton';
import ChatSkeleton from './nav/ChatSkeleton';
import MemoryChip from './nav/MemoryChip';
import CoachMessageBubble from './nav/CoachMessageBubble';
import EndSessionButton from './nav/EndSessionButton';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const fmtNum = (v, unita) => {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  const s = Number.isInteger(n) ? n.toLocaleString('it-IT') : n.toLocaleString('it-IT', { maximumFractionDigits: 2 });
  return unita ? `${s} ${unita}` : s;
};

function shiftWeek(label, delta) {
  // YYYY-Www → shift by delta weeks
  const m = label.match(/^(\d+)-W(\d+)$/);
  if (!m) return label;
  // Use Monday of current week
  const y = parseInt(m[1]), w = parseInt(m[2]);
  // Get monday: ISO week 1 Monday = Jan 4 week
  const jan4 = new Date(y, 0, 4);
  const jan4DayOfWeek = jan4.getDay() || 7;
  const mondayW1 = new Date(jan4); mondayW1.setDate(jan4.getDate() - jan4DayOfWeek + 1);
  const monday = new Date(mondayW1); monday.setDate(mondayW1.getDate() + (w - 1) * 7 + delta * 7);
  // ISO week of monday
  const tmp = new Date(monday); tmp.setHours(0,0,0,0); tmp.setDate(tmp.getDate() + 4 - (tmp.getDay() || 7));
  const yearStart = new Date(tmp.getFullYear(), 0, 1);
  const weekNum = Math.ceil((((tmp - yearStart) / 86400000) + 1) / 7);
  return `${tmp.getFullYear()}-W${String(weekNum).padStart(2, '0')}`;
}

function currentIsoWeek() {
  const d = new Date(); d.setHours(0,0,0,0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  const weekNum = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  return `${d.getFullYear()}-W${String(weekNum).padStart(2, '0')}`;
}


export default function WeeklyReviewChat() {
  const [settimana, setSettimana] = useState(currentIsoWeek());

  const {
    stato,
    aree,
    messages,
    setMessages,
    input,
    setInput,
    sending,
    applying,
    pendingOps,
    setPendingOps,
    editingOpsJson,
    setEditingOpsJson,
    highlightIds,
    scrollRef,
    inputRef,
    handleSend,
    handleKeyDown,
    handleConfirmOps,
    handleCancelOps,
    handleCloseItem,
    handleUpdateItem,
    endSession,
  } = useCoachChat({
    momento: 'settimana',
    fetchStateFn: fetchStateSettimana,
    param: settimana,
    resumeGreetingText: (s) => `Riprendiamo la review della ${s.settimana}.`,
  });

  const handleReplan = async (kr_id, kr_titolo) => {
    try {
      const res = await replanTargetPeriodo({ key_result_id: kr_id, ripartizione: 'a_crescere' });
      // Inietto come messaggio assistant + pendingOps
      const previewTxt = (res.context?.preview || [])
        .map((p) => `  ${p.etichetta}: ${p.suggerito} ${res.context?.unita || ''}`)
        .join('\n');
      const newMsg = {
        role: 'assistant',
        content: `Ricalcolo piano per "${kr_titolo}":\n${res.messaggio}\n\nNuovo piano (a crescere):\n${previewTxt}`,
        operazioni: res.operazioni,
      };
      const next = [...messages, newMsg];
      setMessages(next);
      setPendingOps({ operazioni: res.operazioni, msgIdx: next.length - 1 });
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Errore ricalcolo');
    }
  };

  const handleCopyOpen = async () => {
    // Conta prima le non-chiuse (client-side, evita call inutile)
    const openActions = (stato.azioni || []).filter((a) => !a.fatto && !a.is_occurrence);
    if (openActions.length === 0) {
      toast('Nessuna azione aperta da duplicare.');
      return;
    }
    if (!window.confirm(
      `Duplicare ${openActions.length} azion${openActions.length === 1 ? 'e' : 'i'} non chius${openActions.length === 1 ? 'a' : 'e'} sulla settimana successiva?\n\nLe azioni ricorrenti sono escluse (si rigenerano da sole).`
    )) return;
    try {
      const res = await copyOpenActions(stato.settimana);
      toast.success(`${res.count} azion${res.count === 1 ? 'e' : 'i'} duplicat${res.count === 1 ? 'a' : 'e'} su ${res.to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Duplicazione fallita');
    }
  };

  if (!stato) {
    return (
      <div className="min-h-screen bg-canvas text-ink flex flex-col">
        <div className="flex-1 max-w-[1600px] w-full mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-[1fr_480px] gap-8">
          <ChatSkeleton />
          <CoachStatePanelSkeleton title="Stato della settimana" />
        </div>
      </div>
    );
  }

  const diff = stato.diff_mese || [];
  const offTrack = diff.filter((d) => !d.on_track);
  const onTrack = diff.filter((d) => d.on_track);

  return (
    <div className="min-h-screen bg-canvas text-ink flex flex-col">
      <div className="border-b border-line shrink-0">
        <div className="max-w-[1600px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-soft hover:text-ink transition-colors" data-testid="back-to-dashboard">
              <ArrowLeft size={14} strokeWidth={1.5} /> Dashboard
            </Link>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-mute">/</span>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-ink">Review settimanale</span>
          </div>
          <div className="flex items-center gap-2" data-testid="week-switcher">
            <button onClick={() => setSettimana((s) => shiftWeek(s, -1))} className="h-8 w-8 flex items-center justify-center text-soft hover:bg-ink hover:text-canvas transition-colors" data-testid="week-prev" aria-label="Settimana precedente">
              <ChevronLeft size={14} strokeWidth={1.5} />
            </button>
            <span className="font-display text-xl tracking-tight px-3" data-testid="week-label">{stato.settimana}</span>
            <button onClick={() => setSettimana((s) => shiftWeek(s, +1))} className="h-8 w-8 flex items-center justify-center text-soft hover:bg-ink hover:text-canvas transition-colors" data-testid="week-next" aria-label="Settimana successiva">
              <ChevronRight size={14} strokeWidth={1.5} />
            </button>
            <SendDigestButton kind="weekly" testid="send-weekly-digest-btn" />
            <button
              type="button"
              onClick={handleCopyOpen}
              className="font-mono text-[10px] uppercase tracking-[0.15em] text-soft hover:text-ink border border-line px-2 py-1 hover:border-ink transition-colors ml-2"
              title="Duplica le azioni non chiuse sulla settimana successiva (le ricorrenze sono escluse)"
              data-testid="copy-open-actions-btn"
            >
              Duplica non chiuse →
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 max-w-[1600px] w-full mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-[1fr_500px] gap-8">
        {/* LEFT — Chat */}
        <div className="flex flex-col min-h-0 border border-line bg-paper">
          {/* Diff banner IN CIMA ALLA CHAT */}
          {diff.length > 0 && (
            <div className="border-b border-line bg-canvas" data-testid="diff-banner">
              <div className="px-6 py-3 flex items-center gap-4 border-b border-linesoft">
                <span className="mono-label">Scostamento · {stato.mese_nome}</span>
                <span className="flex-1" />
                <span className="font-mono text-xs">
                  <span className="text-ok" data-testid="diff-ontrack-count">{onTrack.length}</span>
                  <span className="text-mute"> <Check className="inline h-3 w-3" strokeWidth={1.5} /> · </span>
                  <span className="text-accent" data-testid="diff-offtrack-count">{offTrack.length}</span>
                  <span className="text-mute"> <AlertTriangle className="inline h-3 w-3" strokeWidth={1.5} /></span>
                </span>
              </div>
              <ul className="px-6 py-3 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
                {diff.map((d) => (
                  <li key={d.kr_id} className="flex items-baseline gap-2 font-mono text-xs" data-testid="diff-row">
                    {d.on_track
                      ? <CheckCircle2 size={11} strokeWidth={1.8} className="text-ok shrink-0 mt-0.5" />
                      : <AlertTriangle size={11} strokeWidth={1.8} className="text-accent shrink-0 mt-0.5" />}
                    <span className="font-body text-xs truncate flex-1">{d.titolo}</span>
                    <span className="text-soft tabular-nums">{fmtNum(d.valore_attuale, d.unita)}</span>
                    <span className="text-mute">/</span>
                    <span className="text-soft tabular-nums">{fmtNum(d.target_mese_suggerito, d.unita)}</span>
                    <span className={`tabular-nums font-medium ${d.on_track ? 'text-ok' : 'text-accent'}`}>
                      {d.delta_pct > 0 ? '+' : ''}{d.delta_pct}%
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex items-baseline justify-between px-6 py-4 border-b border-line">
            <h2 className="font-display text-lg tracking-tight">QuantumCoach · Settimana</h2>
            <div className="flex items-center gap-3">
              <EndSessionButton onEnd={endSession} testid="settimana-review-end-session-btn" />
              <span className="mono-label">{stato.lun} → {stato.dom}</span>
            </div>
          </div>

          {/* INPUT in alto (sticky) */}
          <div className="border-b border-line p-4 sticky top-0 bg-paper z-10">
            <div className="relative">
              <Textarea ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown}
                placeholder="Reali del KR, retro 3-voci, ostacoli, MIT della settimana dopo — una alla volta."
                className="rounded-none border-line focus-visible:ring-0 focus-visible:border-ink resize-none font-body text-base pr-14 min-h-[88px]"
                disabled={sending} data-testid="chat-input" />
              <button onClick={handleSend} disabled={sending || !input.trim()}
                className="absolute bottom-3 right-3 h-9 w-9 bg-ink text-canvas flex items-center justify-center hover:bg-accent disabled:bg-mute transition-colors"
                data-testid="send-message-btn" aria-label="Invia">
                <Send size={14} strokeWidth={1.8} />
              </button>
            </div>
            {sending && (
              <div className="flex items-center gap-2 text-mute font-mono text-xs mt-3">
                <Loader2 size={14} className="animate-spin" strokeWidth={1.5} />
                {messages.length === 0
                  ? 'QuantumCoach sta preparando il punto della situazione…'
                  : 'QuantumCoach sta pensando…'}
              </div>
            )}
          </div>

          {/* STORICO sotto, in ORDINE INVERSO (più recente in alto) */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6 space-y-6" data-testid="chat-messages">
            <MemoryChip />
            {[...messages].reverse().map((m, revIdx) => {
              if (m.hidden) return null;
              const idx = messages.length - 1 - revIdx;
              const isLast = idx === messages.length - 1;
              return (
                <CoachMessageBubble key={idx} message={m}
                  isPending={pendingOps && pendingOps.msgIdx === idx}
                  editingJson={pendingOps && pendingOps.msgIdx === idx ? editingOpsJson : null}
                  setEditingJson={setEditingOpsJson} applying={applying}
                  onConfirm={handleConfirmOps} onCancel={handleCancelOps}
                  onEdit={() => setEditingOpsJson(JSON.stringify(pendingOps.operazioni, null, 2))}
                  onChipClick={isLast && !sending ? handleSend : null}
                  aree={aree} />
              );
            })}
          </div>
        </div>

        {/* RIGHT — Stato Settimana (collassabile per area) */}
        <CoachStatePanel
          title="Stato della settimana"
          subtitle={`${stato.lun} → ${stato.dom}`}
          obiettivi={stato.obiettivi || []}
          keyResults={stato.key_results || []}
          azioni={stato.azioni || []}
          mit={stato.mit || []}
          storageKey="weekly"
          testid="week-panel"
          highlightIds={highlightIds}
          onCloseItem={handleCloseItem}
          onUpdateItem={handleUpdateItem}
        />
      </div>
    </div>
  );
}

